import { Suspense, useState, useEffect, useRef } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import RootUserLayout from "@/features-root-user/layout";
import {
  useCreateOrganization,
  CreateOrganizationPayload,
  useLogoPresignedUrl, // Import the new hook
  useUpdateOrganization, // Import update hook for logoUrl update
} from "@/features-root-user/service/organization";
import { toast } from "sonner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { EcosuiteErrorBoundary } from "@/components/error-boundary";
import { StatusSelect } from "@/components/status-select";
import { StatusButton } from "@/components/ui/loading-button";
import { OrganizationStatus } from "../../../../../../packages/db/src/generated";
import {
  settingsSchema,
  googleIntegrationSchema,
  softLedgerSchema,
  solarNetworkCredentialSchema,
  dataSharingSchema,
  EnableSharing,
} from "../../../../../../packages/db/src/types/organizationJsonTypes";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Check, Loader2, UploadCloud, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

// Update form schema to include optional logo file
const createFormSchema = z.object({
  organization: z.object({
    name: z.string().min(1, { message: "Organization name is required." }),
    slug: z
      .string()
      .min(1, { message: "Slug is required." })
      .regex(
        /^[a-z0-9]+(-[a-z0-9]+)*[a-z0-9]+$/,
        "Invalid slug format. Use lowercase letters, numbers, and hyphens (no consecutive hyphens, no start/end with hyphen).",
      ),
    domain: z.string().min(1, { message: "Domain is required." }),
    responsiblePerson: z
      .string()
      .min(1, { message: "Responsible person is required." }),
    status: z.nativeEnum(OrganizationStatus),
    nextAccess: z.boolean().default(false),
    settings: settingsSchema,
    googleIntegration: googleIntegrationSchema,
    softLedger: softLedgerSchema,
    solarNetworkCredentials: solarNetworkCredentialSchema,
    dataSharing: dataSharingSchema.optional(),
  }),
  user: z.object({
    firstName: z.string().min(1, { message: "First name is required." }),
    lastName: z.string().min(1, { message: "Last name is required." }),
    email: z
      .string()
      .email({ message: "Invalid email address." })
      .min(1, { message: "Email is required." }),
  }),
  logoFile: z.instanceof(File).optional().nullable(), // Add logo file field
});

type CreateFormData = z.infer<typeof createFormSchema>;

// --- Creation Progress Dialog (unchanged) ---
const OrganizationCreationProgress = ({
  open,
  operationsComplete,
}: {
  open: boolean;
  operationsComplete: boolean;
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const steps = [
    { label: "Initializing organization setup", duration: 500 },
    { label: "Creating organization structure", duration: 800 },
    { label: "Provisioning initial user account", duration: 1000 },
    { label: "Configuring access permissions", duration: 700 },
    { label: "Preparing integrations", duration: 500 },
    { label: "Finalizing deployment", duration: 0 }, // Set to 0 to wait for operations to complete
  ];
  const [displayedStep, setDisplayedStep] = useState(0);

  useEffect(() => {
    if (!open) {
      setDisplayedStep(0); // Reset on close
      return;
    }

    let progressInterval: NodeJS.Timeout;
    const startProgress = () => {
      let currentTime = 0;
      let stepIndex = 0;

      progressInterval = setInterval(() => {
        // For the last step, wait until operations are complete
        if (stepIndex === steps.length - 1) {
          if (operationsComplete) {
            setDisplayedStep((prev) => Math.min(prev + 1, steps.length));
            clearInterval(progressInterval);
          }
          return;
        }

        currentTime += 100; // Update every 100ms
        if (
          stepIndex < steps.length &&
          currentTime >= steps[stepIndex].duration
        ) {
          setDisplayedStep((prev) => Math.min(prev + 1, steps.length));
          currentTime = 0; // Reset time for next step
          stepIndex++;
        }
        if (stepIndex >= steps.length) {
          clearInterval(progressInterval);
        }
      }, 100);
    };

    startProgress();

    return () => {
      clearInterval(progressInterval);
    };
  }, [open, operationsComplete]);

  return (
    <Dialog open={open}>
      <DialogContent className="border-primary/20 from-background to-secondary/10 border bg-gradient-to-b p-4 backdrop-blur-sm sm:max-w-2xl sm:p-6">
        <div className="flex w-full flex-col items-center space-y-4">
          <div className="relative mb-2">
            <h2 className="from-primary to-primary/80 bg-gradient-to-r bg-clip-text text-2xl font-semibold tracking-tight text-transparent">
              Creating Organization
            </h2>
            <div className="via-primary absolute -bottom-2 left-1/2 h-[2px] w-16 -translate-x-1/2 transform bg-gradient-to-r from-transparent to-transparent"></div>
          </div>

          <p className="text-muted-foreground max-w-lg text-center text-sm">
            We're setting up your organization ecosystem. This process involves
            several automated steps.
          </p>

          <div className="mt-1 w-full max-w-lg space-y-3">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 transition-all duration-300"
                style={{ opacity: index <= displayedStep ? 1 : 0.5 }}
              >
                <div
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-300",
                    index < displayedStep
                      ? "border-primary/80 bg-primary/10 text-primary"
                      : index === displayedStep
                        ? "border-primary bg-primary/20 text-primary shadow-[0_0_8px_rgba(var(--primary),0.4)]"
                        : "border-muted bg-background/80",
                  )}
                >
                  {index < displayedStep ? (
                    <Check className="h-4 w-4" />
                  ) : index === displayedStep ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <div className="h-4 w-4" /> // Placeholder for future steps
                  )}
                </div>
                <div className="flex-1">
                  <p
                    className={cn(
                      "text-sm font-medium transition-all duration-300",
                      index < displayedStep
                        ? "text-muted-foreground"
                        : index === displayedStep
                          ? "text-primary"
                          : "text-muted-foreground/60",
                    )}
                  >
                    {step.label}
                  </p>
                  {index === displayedStep && (
                    <div className="via-primary/40 mt-1 h-[2px] animate-pulse bg-gradient-to-r from-transparent to-transparent"></div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-secondary/10 border-secondary/20 mt-4 w-full max-w-lg rounded-lg border p-3">
            <div className="space-y-1 text-center text-xs">
              <p className="text-muted-foreground/80 italic">
                Please wait while we configure the organization...
              </p>
              <div className="flex justify-center space-x-1 pt-1">
                <span
                  className="bg-primary/60 inline-block h-1.5 w-1.5 animate-pulse rounded-full"
                  style={{ animationDelay: "0ms" }}
                ></span>
                <span
                  className="bg-primary/60 inline-block h-1.5 w-1.5 animate-pulse rounded-full"
                  style={{ animationDelay: "300ms" }}
                ></span>
                <span
                  className="bg-primary/60 inline-block h-1.5 w-1.5 animate-pulse rounded-full"
                  style={{ animationDelay: "600ms" }}
                ></span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- Default Values (unchanged) ---
const defaultValues: CreateFormData = {
  organization: {
    name: "",
    slug: "",
    domain: "",
    responsiblePerson: "",
    status: OrganizationStatus.NEW,
    nextAccess: false,
    settings: {
      hideAdvancedGraphs: false,
      isPublic: false,
      logoUrl: "", // Initialize as null
      luckyOrangeSiteId: "",
      projectMilestoneRecipients: [],
      qgisUrl: "",
      salesforceUrl: "",
      slackXbobToken: "",
    },
    googleIntegration: {
      checklistTemplateId: "",
      googleDriveClientEmail: "",
      googleDrivePrivateKey: "",
      projectMilestoneDatesId: "",
      serviceRequestTemplateId: "",
      useEcosuite: false,
    },
    softLedger: {
      clientId: "",
      clientSecret: "",
      tenantUUID: "",
    },
    solarNetworkCredentials: [],
    dataSharing: {
      enableSharing: EnableSharing.DoNotShareMyData,
      shareIfMonetizable: false,
    },
  },
  user: {
    firstName: "",
    lastName: "",
    email: "",
  },
  logoFile: null, // Initialize as null
};

// --- Main component ---
function CreateOrganizationContent() {
  const navigate = useNavigate();
  const createMutation = useCreateOrganization();
  const updateMutation = useUpdateOrganization(); // For updating logoUrl later
  const presignedUrlMutation = useLogoPresignedUrl();
  const [showCreationProgress, setShowCreationProgress] = useState(false);
  const [operationsComplete, setOperationsComplete] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<CreateFormData>({
    resolver: zodResolver(createFormSchema),
    mode: "onBlur",
    defaultValues,
  });

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      form.setValue("logoFile", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      form.setValue("logoFile", null);
      setLogoPreview(null);
    }
  };

  const removeLogo = () => {
    form.setValue("logoFile", null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear the file input
    }
  };

  const uploadLogo = async (
    orgId: string,
    logoFile: File,
  ): Promise<string | null> => {
    try {
      const presignedData = await presignedUrlMutation.mutateAsync({
        orgId,
        filename: logoFile.name,
        contentType: logoFile.type,
      });

      const uploadResponse = await fetch(presignedData.uploadUrl, {
        method: "PUT",
        body: logoFile,
        headers: {
          "Content-Type": logoFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Logo upload failed: ${uploadResponse.statusText}`);
      }
      return presignedData.accessUrl;
    } catch (error) {
      toast.error("Logo upload failed.", { description: String(error) });
      return null;
    }
  };

  const onSubmit = async (data: CreateFormData) => {
    setShowCreationProgress(true);
    setOperationsComplete(false);
    const { logoFile, ...orgPayload } = data;

    try {
      // 1. Create Organization (without logoUrl initially)
      const createdOrg = await createMutation.mutateAsync(
        orgPayload as CreateOrganizationPayload,
      );

      // 2. Upload logo if provided
      if (logoFile && createdOrg?.id) {
        toast.info("Uploading new logo...");
        const newLogoUrl = await uploadLogo(createdOrg.id, logoFile);

        if (newLogoUrl) {
          // 3. Update organization with the logo URL
          await updateMutation.mutateAsync({
            id: createdOrg.id,
            data: {
              ...createdOrg, // Restore full organization data
              settings: {
                ...createdOrg.settings,
                logoUrl: newLogoUrl,
              },
            },
          });
          toast.success("Logo uploaded successfully!");
        } else {
          // If upload failed, notify but still proceed
          toast.error("Logo upload failed, organization created without logo.");
        }
      }

      // All operations complete
      setOperationsComplete(true);

      // Wait briefly to show the completed progress
      setTimeout(() => {
        setShowCreationProgress(false);
        toast.success("Organization created successfully!");
        navigate({ to: "/root-user/organization" });
      }, 800);
    } catch (error) {
      // Error during organization creation itself
      setOperationsComplete(true); // Mark as complete even on error
      setShowCreationProgress(false);
      // The mutation hook already shows an error toast
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-8 p-6">
      <OrganizationCreationProgress
        open={showCreationProgress}
        operationsComplete={operationsComplete}
      />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Organization Basic Information */}
          <div className="space-y-6">
            <div className="border-b pb-2">
              <h2 className="text-2xl font-semibold">
                Organization Information
              </h2>
              <p className="text-muted-foreground text-sm">
                Provide the core details for the new organization.
              </p>
            </div>
            {/* Logo Upload */}
            <FormItem>
              <FormLabel>Organization Logo</FormLabel>
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    "flex h-24 w-24 items-center justify-center rounded-md border-2 border-dashed",
                    logoPreview ? "border-transparent" : "border-muted",
                  )}
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="h-full w-full rounded-md object-contain"
                    />
                  ) : (
                    <UploadCloud className="text-muted-foreground h-8 w-8" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <FormControl>
                    <Input
                      type="file"
                      accept="image/png, image/jpeg, image/gif, image/svg+xml"
                      onChange={handleLogoChange}
                      className="hidden"
                      id="logo-upload"
                      ref={fileInputRef}
                    />
                  </FormControl>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      document.getElementById("logo-upload")?.click()
                    }
                  >
                    {logoPreview ? "Change Logo" : "Upload Logo"}
                  </Button>
                  {logoPreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={removeLogo}
                    >
                      <X className="mr-1 h-4 w-4" /> Remove
                    </Button>
                  )}
                  <p className="text-muted-foreground text-xs">
                    PNG, JPG, GIF, SVG up to 2MB.
                  </p>
                </div>
              </div>
              <FormMessage>
                {form.formState.errors.logoFile?.message}
              </FormMessage>
            </FormItem>
            {/* Other basic info fields... */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="organization.name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Organization Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="organization.slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Slug *</FormLabel>
                    <FormControl>
                      <Input placeholder="organization-slug" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="organization.domain"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Domain *</FormLabel>
                    <FormControl>
                      <Input placeholder="example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="organization.responsiblePerson"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Responsible Person *</FormLabel>
                    <FormControl>
                      <Input placeholder="Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="organization.status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status *</FormLabel>
                    <FormControl>
                      <StatusSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Select status"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="organization.nextAccess"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-medium leading-none">
                        Next Access
                      </FormLabel>
                      <p className="text-muted-foreground text-sm">
                        Grant access to the next major version of the
                        application
                      </p>
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Google Integration */}
          <div className="space-y-6">
            <div className="border-b pb-2">
              <h2 className="text-2xl font-semibold">Google Integration</h2>
              <p className="text-muted-foreground text-sm">
                Configure Google integration settings.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="organization.googleIntegration.useEcosuite"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-medium leading-none">
                        Use Ecosuite
                      </FormLabel>
                      <p className="text-muted-foreground text-sm">
                        Enable Ecosuite integration with Google
                      </p>
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* User Information */}
          <div className="space-y-6">
            <div className="border-b pb-2">
              <h2 className="text-2xl font-semibold">Initial User</h2>
              <p className="text-muted-foreground text-sm">
                Provide details for the first user of this organization.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="user.firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="user.lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div>
              <FormField
                control={form.control}
                name="user.email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl>
                      <Input placeholder="john.doe@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <StatusButton
              type="submit"
              isLoading={
                createMutation.isPending ||
                presignedUrlMutation.isPending ||
                updateMutation.isPending ||
                showCreationProgress
              }
              disabled={
                createMutation.isPending ||
                presignedUrlMutation.isPending ||
                updateMutation.isPending ||
                showCreationProgress
              }
            >
              Create Organization
            </StatusButton>
          </div>
        </form>
      </Form>
    </div>
  );
}

// Export wrapped component
export default function CreateOrganization() {
  return (
    <RootUserLayout
      pageTitle="Create Organization"
      pageDescription="Set up a new organization and its initial user."
    >
      <Suspense
        fallback={
          <LoadingScreen
            text="Loading Create Organization Form..."
            className="h-[calc(100vh-180px)]"
          />
        }
      >
        <EcosuiteErrorBoundary>
          <CreateOrganizationContent />
        </EcosuiteErrorBoundary>
      </Suspense>
    </RootUserLayout>
  );
}
